import React, { memo, useMemo } from 'react';
import { useFormik } from 'formik';
import { date, object } from 'yup';
import { startOfDay } from 'date-fns';
import { Trans } from 'react-i18next';

import { ConfirmationModal } from '~/Components';
import { useDateFns, useLanguage } from '~/Hooks';
import { EventGroup } from '~/Models/DTO/EventGroup.model';
import { KeyboardDatePicker, Typography } from '~/UIKit';
import { Clock } from '~/Components/Icons';
import { IEndEventGroupConfirmationOptions } from '~/Screens/EventGroupList/types';

interface IEditCalendarEventConfirmationModal {
  confirmationOptions: EventGroup;
  onClose: () => void;
  onConfirm: (options: IEndEventGroupConfirmationOptions) => void;
}

export const EndEventGroupConfirmationModal = memo(
  ({ confirmationOptions, onClose, onConfirm }: IEditCalendarEventConfirmationModal) => {
    const { t } = useLanguage();
    const { localeDateFormat } = useDateFns();
    const minDate = useMemo(() => startOfDay(new Date()), []);
    const formik = useFormik({
      initialValues: { endDate: minDate },
      validationSchema: object().shape({ endDate: date().required().min(minDate) }),
      validateOnBlur: true,
      validateOnChange: true,
      onSubmit: onConfirm,
    });

    return (
      <ConfirmationModal
        open
        title={t('eventGroups.endEventGroup.title')}
        cancel={{ action: onClose }}
        confirm={{ action: formik.handleSubmit, title: t('app.confirmationModal.confirm') }}
        destructive
      >
        <Typography variant="body1">
          <Trans
            i18nKey="eventGroups.endEventGroup.message.part1"
            values={{ eventName: confirmationOptions.name }}
            components={{ b: <b /> }}
          />
        </Typography>
        <Typography variant="body1">{t('eventGroups.endEventGroup.message.part2')}</Typography>
        <KeyboardDatePicker
          inputFormat={localeDateFormat}
          label={t('time.ends')}
          icon={<Clock />}
          name="endDate"
          onDateChange={(name, date) => date && formik.setFieldValue(name, new Date(date))}
          value={formik.values.endDate.toISOString()}
          error={!!formik.errors.endDate}
          minDate={minDate}
          disablePast
        />
        <Typography variant="body1">{t('eventGroups.endEventGroup.message.part3')}</Typography>
      </ConfirmationModal>
    );
  },
);
